
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { handleServiceError, withErrorHandling } from "../utils/errorHandling";

/**
 * Service for modifying user profiles
 */
export const profileMutationService = {
  /**
   * Create a new profile if it doesn't exist
   * @param userId The ID of the user
   * @param userMetadata Metadata about the user to save in the profile
   * @returns True if successful, false otherwise
   */
  createUserProfile: withErrorHandling(
    async (userId: string, userMetadata: any): Promise<boolean> => {
      console.log('Creating/updating profile for user:', userId, 'with metadata:', userMetadata);
      
      // Check if profile exists
      const { data: existingProfile, error: checkError } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', userId)
        .maybeSingle();

      if (checkError) {
        console.error('Error checking profile existence:', checkError);
        if (checkError.code !== 'PGRST116') { // This is "no rows returned" error, which is expected
          throw checkError;
        }
      }

      // If profile already exists, don't try to create it
      if (existingProfile) {
        console.log('Profile already exists, no need to create');
        return true;
      }

      // Profile doesn't exist, create it using user metadata
      console.log('Creating new profile with user metadata');
      const firstName = userMetadata?.first_name || userMetadata?.given_name || 'New';
      const lastName = userMetadata?.last_name || userMetadata?.family_name || 'User';
      const familyRole = userMetadata?.family_role || 'parent';
      const genericRole = userMetadata?.generic_family_role || 'parent';
      
      try {
        const { error: createError } = await supabase
          .from('profiles')
          .insert({
            id: userId,
            first_name: firstName,
            last_name: lastName,
            family_role: familyRole,
            generic_family_role: genericRole
          });
        
        if (createError) {
          console.error('Error creating profile:', createError);
          
          // If duplicate key error, the profile was created by another process or the database trigger
          // This is not a real error for us
          if (createError.code === '23505') { // PostgreSQL duplicate key error
            console.log('Profile already exists (duplicate key)');
            return true;
          }
          
          toast.error('Could not create user profile');
          throw createError;
        }
        
        console.log('Profile created successfully');
        toast.success('Profile created successfully');
        return true;
      } catch (error) {
        console.error('Unexpected error creating profile:', error);
        throw error;
      }
    },
    'Error in createUserProfile'
  )
};
